import React, { useEffect, useMemo, useRef, useState } from 'react';
import styles from './HexagonView.module.css';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import { orderBy, cloneDeep, throttle, find } from 'lodash';

const HexagonItem = ({ name, competencyLevel, className }) => {
  return (
    <div
      data-cy="hexagon-item"
      className={clsx(styles.hexagonItem, className)}
      tabIndex={0}
    >
      <div className={styles.hexagonItemBody}>
        <div className={styles.competencyName}>{name}</div>
        <div className={styles.proficiencyLevel}>
          <span>{competencyLevel}</span>
        </div>
      </div>
    </div>
  );
};
HexagonItem.propTypes = {
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  competencyLevel: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  className: PropTypes.string
};
const rowCountMap = {
  mini: 1,
  extraSmall: 2,
  small: 3,
  extraMedium: 4,
  medium: 5,
  large: 6,
  extraLarge: 7
};
const calcClassName = width => {
  let className = 'mini';
  if (width >= 380) {
    className = 'extraSmall';
  }
  if (width >= 540) {
    className = 'small';
  }
  if (width >= 684) {
    className = 'extraMedium';
  }
  if (width >= 836) {
    className = 'medium';
  }
  if (width >= 1000) {
    className = 'large';
  }
  if (width >= 1280) {
    className = 'extraLarge';
  }
  return className;
};
const findSuggestedLevelName = (suggestedLevel, levelDescriptions) => {
  if (!levelDescriptions || !Array.isArray(levelDescriptions)) {
    return '';
  }
  const level = find(levelDescriptions, l => {
    return suggestedLevel === parseInt(l.level);
  });
  return level ? level.name : '';
};

const HexagonView = ({ skillsLists }) => {
  const ref = useRef(null);
  const [className, setClassName] = useState('medium');
  const firstSkills = useMemo(() => orderBy(skillsLists[0] || [], ['name']), [
    skillsLists
  ]);
  const secondSkills = useMemo(() => orderBy(skillsLists[1] || [], ['name']), [
    skillsLists
  ]);
  const skills = useMemo(() => {
    if (secondSkills.length === 0) {
      return firstSkills;
    }
    const rowCount = rowCountMap[className];
    const _firstSkills = cloneDeep(
      firstSkills.map(item => ({ ...item, className: styles.inherit }))
    );
    const _secondSkills = cloneDeep(
      secondSkills.map(item => ({ ...item, className: styles.based }))
    );
    if (rowCount <= 3) {
      return _firstSkills.concat(_secondSkills);
    }

    const firstSkillsLength = _firstSkills.length;
    const secondSkillsLength = _secondSkills.length;
    const count =
      (firstSkillsLength / (firstSkillsLength + secondSkillsLength)) * rowCount;
    let x = Math.round(count);
    let y = rowCount - x;
    if (x > rowCount) {
      x = rowCount - 1;
      y = rowCount - x;
    }
    const result = [];
    while (result.length < firstSkillsLength + secondSkillsLength) {
      result.push(..._firstSkills.splice(0, x), ..._secondSkills.splice(0, y));
    }
    return result;
  }, [firstSkills, secondSkills, className]);
  const skillsListsIsEmpty = useMemo(() => skills.length === 0, [skills]);
  useEffect(() => {
    const handleResize = throttle(() => {
      if (ref.current) {
        const wrapperElement = ref.current.parentElement;
        if (wrapperElement) {
          const { width } = wrapperElement.getBoundingClientRect();
          setClassName(calcClassName(width));
        }
      }
    }, 300);
    window.addEventListener('resize', handleResize);
    handleResize();
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);
  return (
    <div
      data-cy="hexagon-container"
      ref={ref}
      className={clsx(styles.hexagonItemContainer, styles[className])}
    >
      {skillsListsIsEmpty && (
        <p className={styles.emptyMessage}>There were no skills.</p>
      )}
      {skills.map(
        (
          {
            className,
            skillId,
            name,
            description,
            suggestedLevel,
            levelDescriptions
          },
          index
        ) => {
          return (
            <HexagonItem
              key={index}
              id={skillId}
              name={name}
              description={description}
              competencyLevel={findSuggestedLevelName(
                suggestedLevel,
                levelDescriptions
              )}
              className={className}
            />
          );
        }
      )}
    </div>
  );
};
HexagonView.propTypes = {
  skillsLists: PropTypes.array.isRequired
};

export default HexagonView;
